import { CheckCircle2, Clock3, QrCode, Ticket, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Link, useParams } from "react-router-dom";
import { customerApi } from "../../api/client";
import LoadingState from "../../components/common/LoadingState";
import Footer from "../../components/feature/Footer";
import Navbar from "../../components/feature/Navbar";
import { useCustomerAuth } from "../../context/CustomerAuthContext";
import type { Order } from "../../types/api";
import { formatCurrency, formatDateTime, formatEventDate } from "../../utils/format";
import { launchPaystackPopup } from "../../utils/paystack";

function isPaymentPending(status: string) {
  return status === "initiated" || status === "pending";
}

const PAYMENT_POLL_WINDOW_MS = 15 * 60 * 1000;
const ORDER_REFRESH_INTERVAL_MS = 5000;

function getPaymentPollingDeadline(order: Order) {
  const startedAt = order.payment?.initiatedAt || order.createdAt;
  const startedAtMs = Date.parse(startedAt);
  return Number.isFinite(startedAtMs) ? startedAtMs + PAYMENT_POLL_WINDOW_MS : 0;
}

function getPaymentPollingRemainingMs(order: Order) {
  return Math.max(getPaymentPollingDeadline(order) - Date.now(), 0);
}

function hasActivePaymentPollingWindow(order: Order) {
  return isPaymentPending(order.paymentStatus) && getPaymentPollingRemainingMs(order) > 0;
}

export default function OrderConfirmationPage() {
  const { reference = "" } = useParams();
  const { token } = useCustomerAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isResumingPayment, setIsResumingPayment] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentActionError, setPaymentActionError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError("Your session expired. Please sign in again.");
      setIsLoading(false);
      return;
    }

    let active = true;
    let latestOrder: Order | null = null;
    let timeoutId: number | undefined;

    const clearScheduledRefresh = () => {
      if (typeof timeoutId === "number") {
        window.clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };

    const scheduleNextRefresh = (nextOrder: Order | null) => {
      clearScheduledRefresh();
      if (!nextOrder || !hasActivePaymentPollingWindow(nextOrder)) {
        return;
      }

      timeoutId = window.setTimeout(() => {
        void loadOrder(false);
      }, Math.min(ORDER_REFRESH_INTERVAL_MS, getPaymentPollingRemainingMs(nextOrder)));
    };

    const loadOrder = async (withLoading = false) => {
      if (withLoading) {
        setIsLoading(true);
      }

      try {
        const { orders } = await customerApi.myOrders(token, { reference });
        if (active) {
          latestOrder = orders[0] ?? null;
          setOrder(latestOrder);
          setError(latestOrder ? null : "Order not found");
          scheduleNextRefresh(latestOrder);
        }
      } catch (requestError) {
        if (active) {
          if (!latestOrder) {
            setError(requestError instanceof Error ? requestError.message : "Could not load order confirmation");
          }
          scheduleNextRefresh(latestOrder);
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    void loadOrder(true);

    return () => {
      active = false;
      clearScheduledRefresh();
    };
  }, [reference, token]);

  const handleResumePayment = async () => {
    if (!token || !order) {
      return;
    }

    setIsResumingPayment(true);
    setPaymentActionError(null);

    try {
      const resumed = await customerApi.resumePayment(token, order.reference);
      if (!resumed.paymentSession) {
        setOrder(resumed.order);
        return;
      }

      const popupResponse = await launchPaystackPopup(resumed.paymentSession);
      const verified = await customerApi.verifyPayment(token, resumed.order.reference, {
        reference: popupResponse.reference || resumed.paymentSession.reference,
      });
      setOrder(verified.order);
    } catch (requestError) {
      setPaymentActionError(requestError instanceof Error ? requestError.message : "Could not resume payment");
    } finally {
      setIsResumingPayment(false);
    }
  };

  if (isLoading) {
    return (
      <div className="aan-ticket-flow min-h-screen bg-black pt-36 text-white">
        <div className="section-frame">
          <LoadingState label="Loading order confirmation..." />
        </div>
      </div>
    );
  }

  if (!order || error) {
    return (
      <div className="aan-ticket-flow min-h-screen bg-black text-white">
        <Navbar />
        <div className="section-frame pt-36">
          <div className="surface-card px-6 py-12 text-center text-sm font-semibold text-red-500">
            {error ?? "Order not found"}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const isPaid = order.paymentStatus === "paid";
  const isFailed = order.paymentStatus === "failed";
  const pending = isPaymentPending(order.paymentStatus);
  const isPollingActive = hasActivePaymentPollingWindow(order);
  const isPollingExpired = pending && !isPollingActive;
  const lastPaymentUpdate = order.payment?.completedAt || order.payment?.lastPolledAt;

  if (isPollingActive) {
    return (
      <div className="aan-ticket-flow min-h-screen bg-black text-white">
        <Navbar />

        <section className="pb-24 pt-36">
          <div className="section-frame">
            <div className="surface-card mx-auto max-w-3xl overflow-hidden p-8 sm:p-10">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-amber-50 text-amber-700">
                <Clock3 size={28} />
              </div>
              <div className="text-center">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700">
                  <Clock3 size={16} />
                  Checking payment status
                </div>
                <h1 className="font-display text-4xl font-black uppercase leading-none text-foreground sm:text-5xl">Please approve payment on your phone.</h1>
                <p className="mx-auto mt-4 max-w-2xl text-sm leading-8 text-muted-foreground">
                  Your payment request has been initiated. Keep this page open while we confirm Paystack and generate your
                  tickets immediately after verification.
                </p>
              </div>

              <div className="mt-8 rounded-[8px] border border-white/15 bg-black p-6 text-white">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.24em] text-aan-bright">
                      Order reference
                    </div>
                    <div className="mt-2 text-2xl font-bold">{order.reference}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.24em] text-aan-bright">
                      Payment reference
                    </div>
                    <div className="mt-2 text-sm font-semibold text-white">{order.payment?.clientReference || "Pending"}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.24em] text-aan-bright">Network</div>
                    <div className="mt-2 text-sm font-semibold uppercase text-white">{order.payment?.network || "Mobile money"}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.24em] text-aan-bright">Mobile</div>
                    <div className="mt-2 text-sm font-semibold text-white">{order.payment?.mobile || order.customer.phone}</div>
                  </div>
                </div>
              </div>

              <div className="mt-6 rounded-[24px] border border-line bg-surface-muted px-5 py-5">
                <div className="mb-2 text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">Current status</div>
                <div className="text-base font-semibold text-foreground">{order.payment?.providerMessage || "Checking payment status..."}</div>
                <p className="mt-3 text-sm leading-7 text-muted-foreground">
                  This page refreshes automatically and verifies Paystack before tickets are issued. If you closed the
                  payment popup, continue the same payment session here.
                </p>
              </div>

              {paymentActionError && (
                <div className="mt-6 rounded-[24px] border border-red-200 bg-red-50 px-5 py-4 text-sm font-semibold text-red-600">
                  {paymentActionError}
                </div>
              )}

              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-[var(--pp-gold)] border-t-transparent" />
                {order.payment?.provider === "paystack" && (
                  <button
                    type="button"
                    onClick={handleResumePayment}
                    disabled={isResumingPayment}
                    className="rounded-full bg-aan-green px-5 py-3 font-display text-sm font-black uppercase text-black hover:bg-aan-bright disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isResumingPayment ? "Opening Paystack..." : "Continue payment"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    );
  }

  return (
    <div className="aan-ticket-flow min-h-screen bg-black text-white">
      <Navbar />

      <section className="pb-24 pt-36">
        <div className="section-frame">
          <div className="surface-card overflow-hidden p-6 sm:p-8">
            <div className="grid gap-10 lg:grid-cols-[1fr_360px] lg:items-start">
              <div>
                <div
                  className={`mb-4 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${
                    isPaid
                      ? "bg-emerald-50 text-emerald-600"
                      : isFailed
                        ? "bg-red-50 text-red-600"
                        : "bg-amber-50 text-amber-700"
                  }`}
                >
                  {isPaid ? <CheckCircle2 size={16} /> : isFailed ? <XCircle size={16} /> : <Clock3 size={16} />}
                  {isPaid
                    ? "Payment confirmed"
                    : isFailed
                      ? "Payment failed"
                      : isPollingExpired
                        ? "Status saved"
                        : "Payment initiated"}
                </div>
                <h1 className="font-display text-5xl font-black uppercase leading-none text-foreground">
                  {isPaid
                    ? "Your tickets are ready."
                    : isFailed
                      ? "This payment did not complete."
                      : isPollingExpired
                        ? "Latest payment status saved."
                        : "Approve the payment on your phone."}
                </h1>
                <p className="mt-4 max-w-2xl text-sm leading-7 text-muted-foreground">
                  {isPaid
                    ? "Share or save the QR code below. This ticket is also saved under your account, so you can reopen it any time from My Tickets."
                    : isFailed
                      ? "The order is saved in your account with the failed status. You can return to checkout and try again."
                    : isPollingExpired
                        ? "The latest Paystack status is saved on this order. Continue payment or try checkout again if the payment was not completed."
                        : "The order has been saved as initiated. This page refreshes automatically and tickets appear immediately after Paystack verification succeeds."}
                </p>

                <div className="mt-8 rounded-[8px] border border-white/15 bg-black p-6 text-white">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.24em] text-aan-bright">
                        Order reference
                      </div>
                      <div className="mt-2 text-3xl font-bold">{order.reference}</div>
                    </div>
                    <div className="text-sm text-slate-300">
                      {formatEventDate(order.event.date, order.event.time)}
                      <br />
                      {order.event.venue}, {order.event.city}
                    </div>
                  </div>
                </div>

                {order.tickets.length > 0 ? (
                  <div className="mt-8 grid gap-5 md:grid-cols-2">
                    {order.tickets.map((ticket) => (
                      <div key={ticket.code} className="rounded-[8px] border border-line bg-surface-muted p-5">
                        <div className="mb-4 flex items-center justify-between">
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                              {ticket.ticketName}
                            </div>
                            <div className="mt-1 text-lg font-bold text-foreground">{ticket.attendeeName}</div>
                          </div>
                          <div className="rounded-full bg-surface px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                            {ticket.status}
                          </div>
                        </div>
                        <div className="rounded-[8px] bg-white p-4">
                          <QRCodeSVG value={ticket.payload} size={220} className="mx-auto" />
                        </div>
                        <div className="mt-4 text-sm text-muted-foreground">
                          Code: <span className="font-semibold text-foreground">{ticket.code}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-8 rounded-[8px] border border-line bg-surface-muted p-6 text-sm leading-7 text-muted-foreground">
                    {isPollingActive
                      ? "No ticket has been issued yet. Keep this page open while we verify Paystack."
                      : isPollingExpired
                        ? "No QR ticket was issued because payment has not been verified."
                      : "No QR ticket was issued for this payment."}
                  </div>
                )}
              </div>

              <aside className="space-y-6">
                <div className="surface-card p-6">
                  <div className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                    <Ticket size={14} />
                    Order summary
                  </div>
                  <div className="space-y-3 text-sm text-muted-foreground">
                    {order.items.map((item) => (
                      <div key={item.ticketTierId} className="flex items-center justify-between">
                        <span>
                          {item.ticketName} x {item.quantity}
                        </span>
                        <span>{formatCurrency(item.quantity * item.unitPrice)}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between">
                      <span>Service fee</span>
                      <span>{formatCurrency(order.serviceFee)}</span>
                    </div>
                    <div className="flex items-center justify-between border-t border-line pt-3 text-lg font-bold text-foreground">
                      <span>Total</span>
                      <span>{formatCurrency(order.total)}</span>
                    </div>
                  </div>
                </div>

                <div className="surface-card p-6">
                  <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                    <QrCode size={14} />
                    Payment state
                  </div>
                  <div className="space-y-3 text-sm text-muted-foreground">
                    <div className="flex items-center justify-between">
                      <span>Status</span>
                      <span className="font-semibold uppercase">{order.paymentStatus}</span>
                    </div>
                    {order.payment?.providerStatus && (
                      <div className="flex items-center justify-between">
                        <span>Provider</span>
                        <span className="font-semibold uppercase">{order.payment.providerStatus}</span>
                      </div>
                    )}
                    {order.payment?.network && (
                      <div className="flex items-center justify-between">
                        <span>Network</span>
                        <span className="font-semibold uppercase">{order.payment.network}</span>
                      </div>
                    )}
                    {order.payment?.mobile && (
                      <div className="flex items-center justify-between">
                        <span>Mobile</span>
                        <span className="font-semibold">{order.payment.mobile}</span>
                      </div>
                    )}
                    {order.payment?.clientReference && (
                      <div className="flex items-center justify-between gap-4">
                        <span>Payment ref</span>
                        <span className="text-right font-semibold">{order.payment.clientReference}</span>
                      </div>
                    )}
                    {order.payment?.providerMessage && (
                      <div className="rounded-[8px] bg-surface-muted px-4 py-4 text-sm leading-7 text-muted-foreground">
                        {order.payment.providerMessage}
                      </div>
                    )}
                    {lastPaymentUpdate && (
                      <div className="text-xs text-muted-foreground">Updated {formatDateTime(lastPaymentUpdate)}</div>
                    )}
                  </div>
                  <Link
                    to="/tickets"
                    className="mt-4 inline-flex rounded-full bg-aan-green px-4 py-3 font-display text-sm font-black uppercase text-black hover:bg-aan-bright"
                  >
                    Open My Tickets
                  </Link>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
